import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Slider,
  LinearProgress,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Language as LanguageIcon
} from '@mui/icons-material';
import {
  startCrawlSession,
  getCrawlProgress,
  pauseCrawlSession,
  resumeCrawlSession,
  cancelCrawlSession,
  forceCancelCrawlSession,
  getCrawlSessions,
  deleteCrawlSession,
  getCrawlSessionResults
} from '../../services/api';
import { CrawlSession, CrawlProgress, DownloadedWebpage } from '../../types/index';

interface WebpageCrawlingTabProps {
  aiId: string;
  collectionId: string;
  onCrawledWebpagesChange: (webpages: DownloadedWebpage[]) => void;
  onRefreshQnaPairs?: () => void;
}

const WebpageCrawlingTab: React.FC<WebpageCrawlingTabProps> = ({
  aiId,
  collectionId,
  onCrawledWebpagesChange,
  onRefreshQnaPairs
}) => {
  const [startUrl, setStartUrl] = useState('');
  const [maxPages, setMaxPages] = useState(20);
  const [organizationContext, setOrganizationContext] = useState('');
  const [crawling, setCrawling] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Active crawl session tracking
  const [activeCrawl, setActiveCrawl] = useState<CrawlProgress | null>(null);
  const [crawlSessions, setCrawlSessions] = useState<CrawlSession[]>([]);
  
  // Results dialog
  const [resultsDialog, setResultsDialog] = useState(false);
  const [selectedSessionResults, setSelectedSessionResults] = useState<{
    session: CrawlSession;
    webpages: DownloadedWebpage[];
  } | null>(null);

  // Define fetchCrawlSessions before it's used in useEffect
  const fetchCrawlSessions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getCrawlSessions(aiId, collectionId, { limit: 20 });
      const sessions = response.data.data || [];
      setCrawlSessions(sessions);
      
      // Check if there's an active session
      const activeSession = sessions.find((s: CrawlSession) => 
        s.status === 'running' || s.status === 'paused'
      );
      
      if (activeSession) {
        // Get detailed progress for active session
        try {
          const progressResponse = await getCrawlProgress(aiId, collectionId, activeSession.id);
          const progress = progressResponse.data.data;
          
          // Only set as active if it's actually still running/paused
          if (progress.status === 'running' || progress.status === 'paused') {
            setActiveCrawl(progress);
            setCrawling(progress.status === 'running');
          } else {
            // Session was completed but database wasn't updated yet, clear it
            setActiveCrawl(null);
            setCrawling(false);
          }
        } catch (err) {
          console.error('Error getting active crawl progress:', err);
          setActiveCrawl(null);
          setCrawling(false);
        }
      } else {
        // No active sessions found, clear the UI
        setActiveCrawl(null);
        setCrawling(false);
      }
    } catch (err: any) {
      console.error('Error fetching crawl sessions:', err);
      setError('Failed to load crawl sessions');
    } finally {
      setLoading(false);
    }
  }, [aiId, collectionId]);

  // Load crawl sessions on component mount
  useEffect(() => {
    fetchCrawlSessions();
  }, [aiId, collectionId, fetchCrawlSessions]);

  // Poll active crawl progress
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (activeCrawl && (activeCrawl.status === 'running' || activeCrawl.status === 'paused')) {
      interval = setInterval(async () => {
        try {
          const response = await getCrawlProgress(aiId, collectionId, activeCrawl.sessionId);
          const progress = response.data.data;
          
          setActiveCrawl(progress);
          
          // If crawl is completed, stop polling and refresh sessions
          if (progress.status === 'completed' || progress.status === 'error' || progress.status === 'cancelled') {
            setCrawling(false);
            setActiveCrawl(null); // Clear active session to reset UI
            fetchCrawlSessions();
            
            if (progress.status === 'completed') {
              setSuccess(`Crawl completed! Downloaded ${progress.currentPageCount} pages.`);
            } else if (progress.status === 'error') {
              setError('Crawl session encountered an error.');
            } else if (progress.status === 'cancelled') {
              setSuccess('Crawl session was cancelled.');
            }
            
            setTimeout(() => {
              setSuccess(null);
              setError(null);
            }, 5000);
          }
        } catch (err: any) {
          console.error('Error polling crawl progress:', err);
          // Stop polling on error
          setCrawling(false);
          setActiveCrawl(null);
        }
      }, 2000); // Poll every 2 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeCrawl, aiId, collectionId, fetchCrawlSessions]);

  const handleStartCrawl = async () => {
    if (!startUrl.trim()) {
      setError('Please enter a start URL');
      return;
    }

    try {
      setCrawling(true);
      setError(null);
      setSuccess(null);

      // Normalize the URL
      let normalizedUrl = startUrl.trim();
      if (!normalizedUrl.match(/^https?:\/\//i)) {
        normalizedUrl = 'https://' + normalizedUrl;
      }

      console.log('Starting crawl session:', { startUrl: normalizedUrl, maxPages, organizationContext });

      const response = await startCrawlSession(aiId, collectionId, {
        startUrl: normalizedUrl,
        maxPages,
        organizationContext: organizationContext.trim() || undefined
      });

      const sessionId = response.data.data.sessionId;
      
      // Start polling for progress
      const progressResponse = await getCrawlProgress(aiId, collectionId, sessionId);
      setActiveCrawl(progressResponse.data.data);
      
      setSuccess(`Crawl session started successfully!`);
      
      // Refresh sessions list
      fetchCrawlSessions();

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);

    } catch (err: any) {
      console.error('Error starting crawl:', err);
      setError('Failed to start crawl: ' + (err.response?.data?.error || err.message));
      setCrawling(false);
    }
  };

  const handlePauseCrawl = async () => {
    if (!activeCrawl) return;
    
    try {
      await pauseCrawlSession(aiId, collectionId, activeCrawl.sessionId);
      setCrawling(false);
      setSuccess('Crawl session paused');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error pausing crawl:', err);
      setError('Failed to pause crawl');
    }
  };

  const handleResumeCrawl = async () => {
    if (!activeCrawl) return;
    
    try {
      await resumeCrawlSession(aiId, collectionId, activeCrawl.sessionId);
      setCrawling(true);
      setSuccess('Crawl session resumed');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error resuming crawl:', err);
      
      // Check if it's a memory/database sync issue
      if (err.response?.status === 409) {
        setError(`Resume failed: ${err.response.data.error}. Try force cancel to clean up.`);
      } else {
        setError('Failed to resume crawl');
      }
    }
  };

  const handleStopCrawl = async () => {
    if (!activeCrawl) return;
    
    try {
      await cancelCrawlSession(aiId, collectionId, activeCrawl.sessionId);
      setCrawling(false);
      setActiveCrawl(null);
      setSuccess('Crawl session cancelled');
      await fetchCrawlSessions(); // Ensure fresh data
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error cancelling crawl:', err);
      setError('Failed to cancel crawl');
    }
  };

  const handleForceCancel = async () => {
    if (!activeCrawl) return;
    
    try {
      await forceCancelCrawlSession(aiId, collectionId, activeCrawl.sessionId);
      setCrawling(false);
      setActiveCrawl(null);
      setSuccess('Crawl session force cancelled and cleaned up');
      await fetchCrawlSessions(); // Ensure fresh data
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error force cancelling crawl:', err);
      setError('Failed to force cancel crawl');
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    try {
      await deleteCrawlSession(aiId, collectionId, sessionId);
      setSuccess('Crawl session deleted successfully');
      fetchCrawlSessions();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error deleting crawl session:', err);
      setError('Failed to delete crawl session');
    }
  };

  const handleViewResults = async (session: CrawlSession) => {
    try {
      const response = await getCrawlSessionResults(aiId, collectionId, session.id);
      setSelectedSessionResults(response.data.data);
      setResultsDialog(true);
    } catch (err: any) {
      console.error('Error fetching crawl results:', err);
      setError('Failed to load crawl results');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running': return 'primary';
      case 'paused': return 'warning';
      case 'completed': return 'success';
      case 'error': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getProgressPercentage = () => {
    if (!activeCrawl) return 0;
    return Math.round((activeCrawl.currentPageCount / activeCrawl.maxPages) * 100);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body1" color="text.primary" sx={{ mb: 3 }}>
        Crawl an entire website by starting from a homepage. AI will intelligently select relevant pages to download.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Crawl Configuration */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Crawl Configuration
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          <TextField
            label="Start URL (Homepage)"
            placeholder="https://example.com"
            value={startUrl}
            onChange={(e) => setStartUrl(e.target.value)}
            size="small"
            fullWidth
            disabled={crawling}
          />
          
          <TextField
            label="Organization Context (Optional)"
            placeholder="e.g., Educational platform, E-commerce site, Company website..."
            value={organizationContext}
            onChange={(e) => setOrganizationContext(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={2}
            disabled={crawling}
            helperText="Help AI understand what kind of content is relevant"
          />
          
          <Box>
            <Typography gutterBottom>
              Maximum Pages: {maxPages}
            </Typography>
            <Slider
              value={maxPages}
              onChange={(_, value) => setMaxPages(value as number)}
              min={1}
              max={100}
              marks={[
                { value: 1, label: '1' },
                { value: 25, label: '25' },
                { value: 50, label: '50' },
                { value: 75, label: '75' },
                { value: 100, label: '100' }
              ]}
              disabled={crawling}
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              AI will filter links to find the most relevant pages (max 100 pages)
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          color="primary"
          onClick={handleStartCrawl}
          disabled={crawling || !startUrl.trim()}
          startIcon={<PlayArrowIcon />}
          sx={{ mr: 1 }}
        >
          Start Crawling
        </Button>
      </Paper>

      {/* Active Crawl Progress */}
      {activeCrawl && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1">
              Active Crawl Progress
            </Typography>
            <Chip 
              label={activeCrawl.status.toUpperCase()} 
              color={getStatusColor(activeCrawl.status) as any}
              size="small"
            />
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {activeCrawl.currentPageCount} of {activeCrawl.maxPages} pages ({getProgressPercentage()}%)
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={getProgressPercentage()}
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              Discovered: {activeCrawl.discoveredLinksCount} | 
              Queued: {activeCrawl.queuedLinksCount} | 
              Failed: {activeCrawl.failedLinksCount}
            </Typography>
          </Box>
          
          {activeCrawl.currentlyProcessing && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Currently processing: {activeCrawl.currentlyProcessing}
            </Typography>
          )}
          
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {activeCrawl.status === 'running' && (
              <Button
                variant="outlined"
                onClick={handlePauseCrawl}
                startIcon={<PauseIcon />}
                size="small"
              >
                Pause
              </Button>
            )}
            
            {activeCrawl.status === 'paused' && (
              <Button
                variant="outlined"
                onClick={handleResumeCrawl}
                startIcon={<PlayArrowIcon />}
                size="small"
              >
                Resume
              </Button>
            )}
            
            {(activeCrawl.status === 'running' || activeCrawl.status === 'paused') && (
              <Button
                variant="outlined"
                color="error"
                onClick={handleStopCrawl}
                startIcon={<StopIcon />}
                size="small"
              >
                Cancel
              </Button>
            )}
            
            {(activeCrawl.status === 'running' || activeCrawl.status === 'paused') && (
              <Button
                variant="outlined"
                color="error"
                onClick={handleForceCancel}
                size="small"
                sx={{ 
                  opacity: 0.7,
                  '&:hover': { opacity: 1 }
                }}
              >
                Force Cancel
              </Button>
            )}
          </Box>
        </Paper>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* Crawl Sessions History */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Crawl Sessions ({crawlSessions.length})
        </Typography>
        <IconButton onClick={fetchCrawlSessions} size="small" disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {loading && crawlSessions.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : crawlSessions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <LanguageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No crawl sessions yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Start crawling a website to see sessions here
          </Typography>
        </Paper>
      ) : (
        <List>
          {crawlSessions.map((session, index) => (
            <React.Fragment key={session.id}>
              <ListItem sx={{ px: 0 }}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {session.start_url}
                      </Typography>
                      <Chip 
                        label={session.status} 
                        color={getStatusColor(session.status) as any}
                        size="small"
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {session.current_page_count} of {session.max_pages} pages downloaded
                      </Typography>
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        Started: {new Date(session.created_at).toLocaleString()}
                      </Typography>
                      {session.completed_at && (
                        <>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            Completed: {new Date(session.completed_at).toLocaleString()}
                          </Typography>
                        </>
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleViewResults(session)}
                      disabled={session.current_page_count === 0}
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteSession(session.id)}
                      disabled={session.status === 'running'}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
              {index < crawlSessions.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      )}

      {/* Results Dialog */}
      <Dialog
        open={resultsDialog}
        onClose={() => setResultsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Crawl Session Results
          {selectedSessionResults && (
            <Typography variant="subtitle2" color="text.secondary">
              {selectedSessionResults.session.start_url}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedSessionResults && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Downloaded {selectedSessionResults.webpages.length} pages
              </Typography>
              <List dense>
                {selectedSessionResults.webpages.map((webpage) => (
                  <ListItem key={webpage.id}>
                    <ListItemText
                      primary={webpage.url}
                      secondary={
                        <>
                          {webpage.title && webpage.title.trim() && 
                           !webpage.title.toLowerCase().includes('blocked') && 
                           webpage.title !== webpage.url && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                              {webpage.title}
                            </Typography>
                          )}
                          {webpage.page_depth !== undefined && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Depth: {webpage.page_depth} | Q&A pairs: {webpage.qa_pairs_count || 0}
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WebpageCrawlingTab;